import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

import isInternalLink from '@dashboard/services/isInternalLink';

const propTypes = {
  to: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.node,
    PropTypes.element,
  ]).isRequired,
  onClick: PropTypes.func,
  handleClick: PropTypes.func,
  disabled: PropTypes.bool,
  appearance: PropTypes.oneOf([
    'primary',
    'icon-button',
    'bare',
    'secondary',
    'light',
    'link',
    'subtle',
  ]),
  className: PropTypes.string,
};

const defaultProps = {
  to: '',
  handleClick: null,
  disabled: false,
  appearance: 'default',
};

const buttonStyles = css`
  appearance: none;
  display: inline-flex;
  align-items: center;
  flex-shrink: 1;
  cursor: ${p => p.disabled && 'not-allowed'};
  box-shadow: ${p => p.shadow && 'var(--shadow)'};
  transition-property: color, background;
  transition-duration: 0.2s;
  transition-timing-function: ease;

  > svg {
    transition: fill ease-in-out 0.2s;
  }
`;

export const StyledButton = styled.button`
  ${buttonStyles}
`;

const StyledButtonLink = styled(Link)`
  ${buttonStyles}
`;

const StyledButtonAnchor = styled('a')`
  ${buttonStyles}
`;

export function getButtonClass(appearance, size, external, wide) {
  return clsx(
    'disabled:opacity-80 rounded focus:outline-none focus:ring-1 focus:ring-chocolate-200 transition-colors',
    {
      'px-4 text-white font-medium border border-chocolate-1000 hover:border-chocolate-900 bg-chocolate-1000 hover:bg-chocolate-900 focus:outline-none focus:ring-1 focus:ring-chocolate-200':
        appearance === 'primary',
      'text-gray-900 font-medium bg-white border border-gray-200 hover:border-chocolate-600 disabled:hover:border-gray-200 focus:outline-none focus:ring-1 focus:ring-chocolate-200':
        appearance === 'light' || appearance === 'secondary',
      'text-gray-900 focus:outline-none focus:ring-1 focus:ring-chocolate-200 bg-gray-100 hover:bg-gray-200 border border-gray-100 hover:border-gray-200':
        appearance === 'subtle',
      'text-gray-900 hover:text-gray-500': appearance === 'bare',
      'hover:bg-white border border-transparent hover:border-chocolate-600 disabled:hover:border-transparent rounded transition-colors':
        appearance === 'icon-button',
      '!px-1.5': appearance === 'icon-button' && size !== 'narrow',
      '!px-0 py-2 text-base': size === 'narrow',
      'underline hover:text-gray-700 font-medium !p-0': appearance === 'link',
      'px-3 py-[7px] text-base': size === 'base',
      'px-2 py-1 text-sm': size === 'small',
      'w-full text-lg py-3 text-center flex justify-center': size === 'full',
      'inline-flex items-center': external === true,
      'px-5': wide === true,
    },
  );
}

const Button = forwardRef(function Button(
  {
    to,
    href,
    className,
    target,
    size = 'base',
    appearance,
    type = 'button',
    arrowRight = false,
    external = false,
    wide = false,
    handleClick = null,
    ...props
  },
  ref,
) {
  const onClick = props.onClick || handleClick || null;

  const buttonClassName = clsx(className, getButtonClass(appearance, size, external, wide));

  if (href || to) {
    const isInternal = isInternalLink(href || to);
    const isBlank = target === '_blank';

    const Component = isInternal && !isBlank ? StyledButtonLink : StyledButtonAnchor;

    return (
      <Component
        ref={ref}
        className={buttonClassName}
        onClick={onClick}
        href={href || to}
        to={href || to}
        target={target}
        rel={isBlank ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {props.children}

        {external && <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2" />}

        {arrowRight && <ArrowRightIcon className="w-4 h-4 ml-2" />}
      </Component>
    );
  }

  return (
    <StyledButton
      ref={ref}
      className={buttonClassName}
      appearance={props.appearance}
      onClick={onClick}
      target={target}
      type={type}
      {...props}
    >
      {props.children}

      {arrowRight && <ArrowRightIcon className="w-4 h-4 ml-2" />}
    </StyledButton>
  );
});

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
